// File: frontend/src/components/ai/AgentTable.js

import React from 'react';
import { Eye, ChevronRight, MessageSquare } from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import EmptyState from '../utility/EmptyState';
import styles from './AgentTable.module.css';

const AgentTable = ({ 
  agents, 
  isLoading, 
  onViewDetails,
  onStartChat
}) => {
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getBadgeClass = (value, type) => {
    if (type === 'status') {
      switch (value) {
        case 'active': return styles.badgeSuccess;
        case 'inactive': return styles.badgeWarning;
        case 'deprecated': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    if (type === 'deployment') {
      switch (value) {
        case 'deployed': return styles.badgeSuccess;
        case 'pending': return styles.badgeWarning;
        case 'failed': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    return styles.badgeDefault;
  };

  if (isLoading && agents.length === 0) {
    return (
      <div className={styles.tableCard}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="large" text="Loading agents..." />
        </div>
      </div>
    );
  }

  if (!isLoading && agents.length === 0) {
    return (
      <div className={styles.tableCard}>
        <EmptyState
          title="No agents found"
          message="No AI agents available for your enterprise"
        />
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      {/* Header */}
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>
          AI Agents
          <span className={styles.agentCount}>({agents.length})</span>
        </h2>
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileView}>
        {agents.map((agent) => (
          <div key={`${agent.agent_name}-${agent.version}`} className={styles.agentCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <div className={styles.agentName}>{agent.agent_name}</div>
                <div className={styles.agentTagline}>{agent.marketing_tagline}</div>
              </div>
              <button
                onClick={() => onViewDetails(agent)}
                className={styles.viewButtonMobile}
                aria-label="View agent details"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Version:</span>
                <span className={styles.cardValue}>{agent.version}</span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Status:</span>
                <span className={`${styles.badge} ${getBadgeClass(agent.agent_status, 'status')}`}>
                  {agent.agent_status}
                </span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Deployment:</span>
                <span className={`${styles.badge} ${getBadgeClass(agent.deployment_status, 'deployment')}`}>
                  {agent.deployment_status}
                </span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Model:</span>
                <span className={styles.cardValue}>{agent.primary_model_provider}</span>
              </div>

              <div className={styles.cardActions}>
                <button
                  onClick={() => onStartChat(agent)}
                  className={styles.chatButton}
                  disabled={agent.agent_status !== 'active'}
                >
                  <MessageSquare size={16} />
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className={styles.desktopView}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Agent Name</th>
                <th>Version</th>
                <th>Status</th>
                <th>Deployment</th>
                <th>Model Provider</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={`${agent.agent_name}-${agent.version}`}>
                  <td className={styles.nameCell}>
                    <div className={styles.agentName}>{agent.agent_name}</div>
                    <div className={styles.agentTagline}>{agent.marketing_tagline}</div>
                  </td>

                  <td className={styles.versionCell}>
                    {agent.version}
                    {agent.is_latest && (
                      <span className={`${styles.badge} ${styles.badgeSmall} ${styles.badgeInfo}`}>
                        Latest
                      </span>
                    )}
                  </td>

                  <td>
                    <span className={`${styles.badge} ${getBadgeClass(agent.agent_status, 'status')}`}>
                      {agent.agent_status}
                    </span>
                  </td>

                  <td>
                    <span className={`${styles.badge} ${getBadgeClass(agent.deployment_status, 'deployment')}`}>
                      {agent.deployment_status}
                    </span>
                  </td>

                  <td className={styles.modelCell}>
                    {agent.primary_model_provider}
                  </td>

                  <td className={styles.dateCell}>
                    {formatDate(agent.updated_at)}
                  </td>

                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        onClick={() => onViewDetails(agent)}
                        className={styles.viewButton}
                        aria-label="View agent details"
                      >
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        onClick={() => onStartChat(agent)}
                        className={styles.chatButtonDesktop}
                        disabled={agent.agent_status !== 'active'}
                        aria-label="Start chat with agent"
                      >
                        <MessageSquare size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentTable;